import type { Problem } from '@selflevel/contracts';

/**
 * LA ÚNICA PUERTA AL SERVIDOR.
 *
 * Todo pasa por aquí, y aquí pasan tres cosas que no deben repetirse por el
 * código: la cabecera del dispositivo, la traducción de un Problem Details a un
 * error tipado, y **la distinción entre «no hay red» y «el servidor dijo que no»**.
 *
 * Esa distinción es la que gobierna la outbox. Un fallo de red es reintentable
 * para siempre; un 400 no lo es nunca, y confundirlos produce uno de los dos
 * desastres: una cola que se reintenta infinitamente contra un error permanente, o
 * un dato del usuario que se tira porque el metro entró en un túnel.
 */

/** Un solo origen para SPA y API (PRD 3.4): rutas relativas, sin CORS. */
const BASE = '/api';

export class ErrorDeApi extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  readonly traceId: string | undefined;
  /** Segundos que el servidor pide esperar, si los pidió. */
  readonly retryAfterS: number | undefined;

  constructor(args: {
    status: number;
    code: string;
    retryable: boolean;
    detail?: string;
    traceId?: string;
    retryAfterS?: number;
  }) {
    super(args.detail ?? args.code);
    this.name = 'ErrorDeApi';
    this.status = args.status;
    this.code = args.code;
    this.retryable = args.retryable;
    this.traceId = args.traceId;
    this.retryAfterS = args.retryAfterS;
  }
}

/**
 * No hubo respuesta: avión, túnel, servidor caído, DNS. **Siempre reintentable.**
 * Es una clase aparte y no un `ErrorDeApi` con `status: 0` para que sea imposible
 * tratarlo por accidente como una respuesta del servidor.
 */
export class SinRed extends Error {
  constructor(causa: unknown) {
    super('sin conexión con el servidor', { cause: causa });
    this.name = 'SinRed';
  }
}

export type OpcionesPeticion = {
  readonly deviceId: string;
  readonly metodo?: 'GET' | 'POST' | 'DELETE';
  readonly cuerpo?: unknown;
  readonly signal?: AbortSignal;
};

/** Versión del protocolo que habla este cliente (PRD 12.4). */
export const VERSION_CLIENTE = 1;

export async function pedir<T>(ruta: string, opciones: OpcionesPeticion): Promise<T> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      method: opciones.metodo ?? 'GET',
      headers: {
        'x-selflevel-device': opciones.deviceId,
        'x-selflevel-client': String(VERSION_CLIENTE),
        ...(opciones.cuerpo === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(opciones.cuerpo === undefined ? {} : { body: JSON.stringify(opciones.cuerpo) }),
      // La cookie de sesión es `sameSite: strict` y el origen es el mismo, así que
      // `same-origin` basta y no se abre la puerta a mandarla a ningún otro sitio.
      credentials: 'same-origin',
      ...(opciones.signal ? { signal: opciones.signal } : {}),
    });
  } catch (causa) {
    throw new SinRed(causa);
  }

  if (respuesta.ok) {
    if (respuesta.status === 204) return undefined as T;
    return (await respuesta.json()) as T;
  }

  const reintentar = respuesta.headers.get('retry-after');
  const problema = await leerProblema(respuesta);

  throw new ErrorDeApi({
    status: respuesta.status,
    code: problema?.code ?? `http-${respuesta.status}`,
    /**
     * `retryable` lo decide el servidor, no el código de estado. Es el campo que
     * el PRD 12.3 pone en el Problem Details precisamente para esto, y no es
     * decorativo: la outbox lo lee para elegir entre reintentar y retirar.
     *
     * Si falta —una respuesta que no es nuestra, un proxy que se interpuso— se
     * asume reintentable solo para 5xx y 429. Suponer lo contrario tiraría datos
     * del usuario ante un 502 de Traefik.
     */
    retryable: problema?.retryable ?? (respuesta.status >= 500 || respuesta.status === 429),
    ...(problema?.detail ? { detail: problema.detail } : {}),
    ...(problema?.traceId ? { traceId: problema.traceId } : {}),
    ...(reintentar ? { retryAfterS: Number.parseInt(reintentar, 10) || 0 } : {}),
  });
}

async function leerProblema(respuesta: Response): Promise<Problem | null> {
  try {
    const tipo = respuesta.headers.get('content-type') ?? '';
    if (!tipo.includes('json')) return null;
    return (await respuesta.json()) as Problem;
  } catch {
    return null;
  }
}
